import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  Spinner,
  Flex,
  VStack,
  HStack,
  useDisclosure,
  Heading
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { getBBPSComplaints, createBBPSComplaint, updateBBPSComplaint, clearBBPSComplaints, setBBPSSearchOrderId, setBBPSCurrentOrderId } from '@store/actions/bbpsActions';
import { setAlert } from '@store/actions/alertActions';
import { MdAdd, MdEdit } from 'react-icons/md';
import ModalLayout from '@components/Modal/ModalLayout';
import DynamicInputModal from '@components/DynamicInputModal';
import GenericCard from '@components/GenericCard';
import SearchBox from '@components/SearchBox/SearchBox';
import { CARD_TYPE, ROLES } from '@constants';
import useAuthRole from '@hooks/useAuthRole';
import { BBPS_COMPLAINT_FORM_CONFIG } from './bbpsComplaintFormConfig';
import { BBPS_COMPLAINT_UPDATE_FORM_CONFIG } from './bbpsComplaintUpdateFormConfig';
import { ALERT_STATUS_TYPE } from '@constants';
import { getComplaintStatusColor } from './utils';

const BBPSComplaints = () => {
  const dispatch = useDispatch();
  const { hasAnyAuthority } = useAuthRole();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: complaints, isLoading, isError, error, isCreating, isUpdating, currentOrderId, searchOrderId } = useSelector(
    (state) => state.bbps.bbpsComplaints
  );

  const [editingComplaint, setEditingComplaint] = useState(null);
  const {
    isOpen: isUpdateModalOpen,
    onOpen: onUpdateModalOpen,
    onClose: onUpdateModalClose
  } = useDisclosure();

  // Check if we already have complaints data
  const hasComplaintsData = complaints && complaints.length > 0;

  // Fetch complaints when orderId is provided
  const fetchComplaints = (orderId) => {
    if (orderId && orderId.trim()) {
      dispatch(getBBPSComplaints({
        orderId: orderId.trim(),
        onError: (error) => console.error('Failed to fetch complaints:', error)
      }));
      dispatch(setBBPSCurrentOrderId(orderId.trim()));
    }
  };

  // Handle search submission
  const handleSearch = () => {
    fetchComplaints(searchOrderId);
  };

  // Handle search change from SearchBox
  const handleSearchChange = ({ searchKey, searchValue }) => {
    dispatch(setBBPSSearchOrderId(searchValue));
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => dispatch(clearBBPSComplaints());
  }, [dispatch]);

  const handleFormSubmit = (formData) => {
    dispatch(
      createBBPSComplaint({
        payload: formData,
        onSuccess: () => {
          dispatch(setAlert({
            title: 'Complaint created successfully',
            status: ALERT_STATUS_TYPE.SUCCESS
          }));
          onClose();
          // Refresh complaints list
          dispatch(getBBPSComplaints({
            orderId: formData?.orderId,
            onError: (error) => console.error('Failed to fetch complaints:', error)
          }));
        },
        onError: (error) => console.error('Failed to create complaint:', error)
      })
    );
  };

  const handleUpdateFormSubmit = (formData) => {
    if (!editingComplaint) return;

    dispatch(
      updateBBPSComplaint({
        complaintId: editingComplaint.complaintId,
        payload: formData,
        onSuccess: () => {
          dispatch(setAlert({
            title: 'Complaint updated successfully',
            status: ALERT_STATUS_TYPE.SUCCESS
          }));
          setEditingComplaint(null);
          onUpdateModalClose();
          // Refresh complaints list
          dispatch(getBBPSComplaints({
            orderId: currentOrderId,
            onError: (error) => console.error('Failed to fetch complaints:', error)
          }));
        },
        onError: (error) => console.error('Failed to update complaint:', error)
      })
    );
  };

  const handleEditComplaint = (complaint) => {
    setEditingComplaint(complaint);
    onUpdateModalOpen();
  };

  const getUpdateFormConfig = () => {
    if (!editingComplaint) return BBPS_COMPLAINT_UPDATE_FORM_CONFIG;

    return BBPS_COMPLAINT_UPDATE_FORM_CONFIG.map(field => ({
      ...field,
      value: editingComplaint[field.name] || ''
    }));
  };



  const canManageComplaints = hasAnyAuthority(ROLES.ADMIN_ROLE, ROLES.BBPS_MANAGER_ROLE);

  const canCreateComplaint = hasAnyAuthority(ROLES.ADMIN_ROLE, ROLES.BBPS_MANAGER_ROLE);

  const renderComplaintModals = () => (
    <>
      {canCreateComplaint && (
        <ModalLayout isOpen={isOpen} onClose={onClose}>
          <DynamicInputModal
            title="Create New Complaint"
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={handleFormSubmit}
            fields={BBPS_COMPLAINT_FORM_CONFIG}
            isLoading={isCreating}
          />
        </ModalLayout>
      )}
      {canManageComplaints && editingComplaint && (
        <ModalLayout isOpen={isUpdateModalOpen} onClose={onUpdateModalClose}>
          <DynamicInputModal
            title="Update BBPS Complaint"
            isOpen={isUpdateModalOpen}
            onClose={onUpdateModalClose}
            onSubmit={handleUpdateFormSubmit}
            fields={getUpdateFormConfig()}
            isLoading={isUpdating}
          />
        </ModalLayout>
      )}
    </>
  );

  return (
    <VStack alignItems="stretch" gap={6} width="100%">
      {/* Header */}
      <HStack justifyContent="space-between" alignItems="center">
        <Heading color="whiteAlpha.700" fontSize="2xl" fontWeight="500">
          BBPS Complaint Management
        </Heading>
        {canCreateComplaint && (
          <Button
            leftIcon={<MdAdd />}
            onClick={onOpen}
            size="sm"
            colorScheme="brand"
          >
            Create Complaint
          </Button>
        )}
      </HStack>

      {/* Search Box */}
      <SearchBox
        searches={[
          {
            searchKey: 'orderId',
            display: 'Order ID',
            placeholder: 'Enter Order ID to search complaints',
            isDefault: true
          }
        ]}
        handleSearchChange={handleSearchChange}
      />

      {/* Search Button */}
      <HStack
        w={'100%'}
        justifyContent={'space-between'}
        maxW={'40rem'}
        alignSelf={'flex-start'}
      >
        <Button
          type="submit"
          color="black"
          rounded="lg"
          colorScheme="brand"
          my={5}
          py={2}
          onClick={handleSearch}
          fontWeight={600}
          isLoading={isLoading}
          alignSelf="start"
          style={{
            marginTop: '1em'
          }}
          size={{
            base: 'md',
            md: 'lg'
          }}
          disabled={!searchOrderId.trim()}
        >
          Search
        </Button>
        {currentOrderId && (
          <Text color="whiteAlpha.500" fontSize="sm" alignSelf="center">
            Currently showing complaints for Order ID: <strong>{currentOrderId}</strong>
          </Text>
        )}
      </HStack>

      {/* Content */}
      {!currentOrderId ? (
        <Box textAlign="center" py={10}>
          <Text color="whiteAlpha.600" fontSize="lg" mb={2}>
            Enter an Order ID above to search for complaints
          </Text>
          <Text color="whiteAlpha.500" fontSize="sm">
            Use the search bar to find complaints associated with a specific order
          </Text>
        </Box>
      ) : isLoading ? (
        <Flex justifyContent="center" alignItems="center" py={10}>
          <Spinner thickness="4px" size="lg" color="colorPrimary" />
        </Flex>
      ) : isError ? (
        <Box color="red.500" p={4} borderRadius="md" borderWidth="1px">
          <Text>Error fetching complaints: {error?.message}</Text>
        </Box>
      ) : complaints?.length > 0 ? (
        <VStack alignItems="stretch" gap={2}>
          {complaints.map((complaint, index) => (
            <GenericCard
              key={complaint.complaintId || index}
              cardData={complaint}
              cardType={CARD_TYPE.BBPS_COMPLAINT_CARD}
              status={{
                text: complaint.status,
                color: getComplaintStatusColor(complaint.status)
              }}
              ActionButtons={
                canManageComplaints
                  ? [
                    <Button
                      key="edit"
                      size="sm"
                      colorScheme="blue"
                      leftIcon={<MdEdit />}
                      onClick={() => handleEditComplaint(complaint)}
                    >
                      Edit
                    </Button>
                  ]
                  : [] 
              }
            />
          ))}
        </VStack>
      ) : (
        <Box textAlign="center" py={10}>
          <Text color="whiteAlpha.600" fontSize="lg" mb={2}>
            No complaints found for Order ID: {currentOrderId}
          </Text>
          <Text color="whiteAlpha.500" fontSize="sm">
            Try searching for a different Order ID or create a new complaint
          </Text>
        </Box>
      )}

      {/* Modals */}
      {renderComplaintModals()}
    </VStack>
  );
};

export default BBPSComplaints;
